/**
 * Snapshot posisi playback terakhir yang diterima dari sebuah webview.
 *
 * @typedef {Object} PlaybackTimingSample
 * @property {number} playbackTimestamp - Posisi video sebagai Unix timestamp dalam milidetik.
 * @property {number} receivedAt - Waktu penerimaan snapshot berdasarkan `performance.now()`.
 */

/**
 * Map sample playback berdasarkan channel.
 *
 * @typedef {Object.<number, PlaybackTimingSample>} PlaybackTimingSamples
 */

/**
 * Map status hidden-pause berdasarkan channel.
 * Nilai `true` berarti channel sedang dihentikan sementara oleh mekanisme sinkronisasi.
 *
 * @typedef {Object.<number, boolean>} PausedChannels
 */

/**
 * Map waktu dimulainya hidden-pause berdasarkan channel.
 * Nilainya berasal dari clock yang sama dengan `receivedAt`, yaitu `performance.now()`.
 *
 * @typedef {Object.<number, number>} PausedAtPerChannel
 */

/**
 * Hasil keputusan untuk satu siklus pemeriksaan sinkronisasi.
 * Sebuah channel tidak akan muncul pada kedua array secara bersamaan.
 *
 * @typedef {Object} PlaybackSyncDecision
 * @property {number[]} channelsToPause - Channel yang perlu dihentikan sementara.
 * @property {number[]} channelsToResume - Channel hidden-pause yang perlu dilanjutkan bersama.
 */

/**
 * Options untuk menentukan tindakan sinkronisasi playback.
 *
 * @typedef {Object} PlaybackSyncOptions
 * @property {number[]} channels - Daftar channel aktif yang akan dibandingkan.
 * @property {PlaybackTimingSamples} samples - Snapshot playback terakhir per channel.
 * @property {PausedChannels} pausedChannels - Status hidden-pause per channel.
 * @property {PausedAtPerChannel} pausedAtPerChannel - Waktu dimulainya hidden-pause per channel.
 * @property {number} now - Waktu pemeriksaan berdasarkan `performance.now()`.
 * @property {number} pauseThresholdMs - Selisih minimum dari channel terlambat untuk memicu pause.
 * @property {number} resumeThresholdMs - Jarak maksimum untuk melanjutkan channel yang dipause.
 * @property {number} minimumPauseDurationMs - Durasi minimum hidden-pause sebelum boleh resume.
 */

/**
 * Memproyeksikan posisi playback sebuah channel ke waktu pemeriksaan yang sama.
 *
 * Snapshot dari masing-masing webview dapat diterima pada waktu berbeda. Untuk channel yang masih
 * berjalan, selisih antara `now` dan `receivedAt` ditambahkan ke posisi terakhir agar seluruh
 * channel dapat dibandingkan pada satu titik waktu. Posisi channel yang dipause tidak dimajukan.
 *
 * `receivedAt` dan `now` harus menggunakan clock monotonic yang sama, biasanya
 * `performance.now()`. `playbackTimestamp` tetap menggunakan Unix timestamp dalam milidetik.
 *
 * @param {PlaybackTimingSample} sample - Snapshot playback terakhir dari channel.
 * @param {boolean} isPaused - Apakah channel sedang hidden-pause.
 * @param {number} now - Waktu pemeriksaan berdasarkan `performance.now()`.
 * @returns {number|null} Estimasi Unix timestamp playback saat ini, atau `null` jika tidak valid.
 *
 * @example
 * const sample = {
 *   playbackTimestamp: Date.parse('2026-09-15T02:15:30.000Z'),
 *   receivedAt: 10000,
 * }
 *
 * getProjectedPlaybackTime(sample, false, 10500)
 * // Date.parse('2026-09-15T02:15:30.500Z')
 */
export const getProjectedPlaybackTime = (sample, isPaused, now) => {
  if (
    !sample ||
    !Number.isFinite(sample.playbackTimestamp) ||
    !Number.isFinite(sample.receivedAt) ||
    !Number.isFinite(now)
  ) {
    return null
  }

  if (isPaused) return sample.playbackTimestamp

  return sample.playbackTimestamp + Math.max(now - sample.receivedAt, 0)
}

/**
 * Menentukan channel yang perlu dipause atau di-resume untuk menyelaraskan playback.
 *
 * Ketika belum ada channel yang dipause, projected time paling lambat digunakan sebagai acuan.
 * Semua channel yang unggul setidaknya sebesar `pauseThresholdMs` dimasukkan ke
 * `channelsToPause`. Ketika koreksi sedang berjalan, fungsi menunggu minimum pause selesai dan
 * channel yang tetap berjalan mendekati posisi paling awal dari kelompok yang dipause. Setelah
 * jaraknya memenuhi `resumeThresholdMs`, seluruh channel yang dipause dilanjutkan bersama.
 *
 * Channel tanpa sample valid diabaikan. Jika tersisa kurang dari dua channel valid, tidak ada
 * tindakan yang dihasilkan. Fungsi ini murni menghitung keputusan dan tidak mengirim postMessage.
 *
 * @param {PlaybackSyncOptions} options - Data dan konfigurasi satu siklus sinkronisasi.
 * @returns {PlaybackSyncDecision} Daftar channel yang perlu dipause dan di-resume.
 *
 * @example
 * const now = performance.now()
 * const decision = getPlaybackSyncDecision({
 *   channels: [1, 2, 3],
 *   samples: {
 *     1: { playbackTimestamp: Date.parse('2026-09-15T02:15:38.200Z'), receivedAt: now },
 *     2: { playbackTimestamp: Date.parse('2026-09-15T02:15:38.000Z'), receivedAt: now },
 *     3: { playbackTimestamp: Date.parse('2026-09-15T02:15:34.000Z'), receivedAt: now },
 *   },
 *   pausedChannels: {},
 *   pausedAtPerChannel: {},
 *   now,
 *   pauseThresholdMs: 500,
 *   resumeThresholdMs: 150,
 *   minimumPauseDurationMs: 300,
 * })
 *
 * // decision = { channelsToPause: [1, 2], channelsToResume: [] }
 */
export const getPlaybackSyncDecision = ({
  channels,
  samples,
  pausedChannels,
  pausedAtPerChannel,
  now,
  pauseThresholdMs,
  resumeThresholdMs,
  minimumPauseDurationMs,
}) => {
  const channelTimes = channels.reduce((result, channel) => {
    const isPaused = Boolean(pausedChannels[channel])
    const projectedTime = getProjectedPlaybackTime(samples[channel], isPaused, now)

    if (projectedTime !== null) result.push({ channel, projectedTime, isPaused })
    return result
  }, [])

  if (channelTimes.length < 2) {
    return { channelsToPause: [], channelsToResume: [] }
  }

  const paused = channelTimes.filter(({ isPaused }) => isPaused)

  if (paused.length > 0) {
    const hasCompletedMinimumPause = paused.every(
      ({ channel }) =>
        Number.isFinite(pausedAtPerChannel[channel]) &&
        now - pausedAtPerChannel[channel] >= minimumPauseDurationMs
    )

    if (!hasCompletedMinimumPause) {
      return { channelsToPause: [], channelsToResume: [] }
    }

    const running = channelTimes.filter(({ isPaused }) => !isPaused)

    if (running.length === 0) {
      return {
        channelsToPause: [],
        channelsToResume: paused.map(({ channel }) => channel),
      }
    }

    const slowestRunningTime = Math.min(...running.map(({ projectedTime }) => projectedTime))
    const earliestPausedTime = Math.min(...paused.map(({ projectedTime }) => projectedTime))

    if (slowestRunningTime >= earliestPausedTime - resumeThresholdMs) {
      return {
        channelsToPause: [],
        channelsToResume: paused.map(({ channel }) => channel),
      }
    }

    return { channelsToPause: [], channelsToResume: [] }
  }

  const slowestTime = Math.min(...channelTimes.map(({ projectedTime }) => projectedTime))
  const channelsToPause = channelTimes
    .filter(({ projectedTime }) => projectedTime - slowestTime >= pauseThresholdMs)
    .map(({ channel }) => channel)

  return { channelsToPause, channelsToResume: [] }
}
